/**
 * Handshake frames: ClientHello (client→server) and Welcome (server→client).
 * (§9.4 join handoff, §12.6 version gate, §15.4 no JSON on the wire)
 *
 * ── ClientHello ─────────────────────────────────────────────────────────────
 *
 * The very first frame a client sends. Its payload is the RAW utf8 bytes of the
 * signed join token minted by the lobby (@fg/lobby signJoinToken) — not JSON,
 * not bit-packed. The token is already a compact `body.sig` string; wrapping it
 * in anything else would be pure ceremony. The server verifies it with the
 * shared LOBBY_SECRET before admitting the connection.
 *
 * ── Welcome ─────────────────────────────────────────────────────────────────
 *
 * The server's acceptance frame. It carries EVERYTHING a client needs to build
 * the byte-identical WorldState the server is simulating — via `createWorld` /
 * `createMatchConfig` — and to know which player it drives, so its prediction
 * (§2.3) runs the same code path as the server's authority. The server is
 * authoritative for match config: the client does not choose it, it learns it
 * here. A full snapshot follows immediately to seed the initial state.
 *
 * ── The roster is on the wire, and it has to be (§4.8, §8.1) ────────────────
 *
 * Snapshots deliberately omit attributes/positions/roles — they are static for
 * the whole match, so the design assumes the client already holds them. But
 * nothing else sends them, so before this frame carried them the client predicted
 * a NEUTRAL 50-across roster while the server ran the real one: every stat-driven
 * quantity (acceleration, error cones, reach) differed, and §8.1's ±15% cap was
 * the only thing keeping the drift under POSITION_EPSILON. It also left every
 * client player's position at the createWorld() default of 'CM', which silently
 * no-op'd playerSwitch's GK exclusion. So the Welcome carries, per active player,
 * its position, role and all 34 attributes; the client applies them to its world
 * with `applyRosterToWorld` BEFORE its first predict, and the two sides simulate
 * the same stats. Attributes are 0..100 integers, sent as u8 — exact, no
 * quantization drift, so prediction stays bit-for-bit on the roster.
 *
 * CHANGING THIS LAYOUT IS A PROTOCOL BREAK. Bump PROTOCOL_VERSION (§12.6); the
 * `protocolVersion` field below is exactly how a client detects the mismatch.
 */

import type { Attributes, Position, Role, SquadSize, WorldState } from '../sim/types.js';
import { MAX_PLAYERS } from '../sim/types.js';
import { BitReader, BitWriter } from './bitwriter.js';
import { MessageType } from './protocol.js';

// ---------------------------------------------------------------------------
// ClientHello — raw utf8 token, framed with a leading type byte.
// ---------------------------------------------------------------------------

const utf8Encoder = new TextEncoder();
const utf8Decoder = new TextDecoder();

/**
 * Build a complete ClientHello frame: `[0x01][utf8(token)]`. Unlike the
 * bit-packed frames this returns the whole frame (there is no BitWriter payload
 * to share), because the token is opaque bytes.
 */
export function encodeClientHello(token: string): Uint8Array {
  const body = utf8Encoder.encode(token);
  const frame = new Uint8Array(body.length + 1);
  frame[0] = MessageType.ClientHello;
  frame.set(body, 1);
  return frame;
}

/** Recover the token string from a received ClientHello frame (drops byte 0). */
export function decodeClientHello(frame: Uint8Array): string {
  return utf8Decoder.decode(frame.subarray(1));
}

// ---------------------------------------------------------------------------
// Welcome — bit-packed payload (the MessageType byte is added by protocol.ts).
// ---------------------------------------------------------------------------

/** Everything a client needs to reconstruct the server's WorldState + its seat. */
export interface WelcomePayload {
  /** PROTOCOL_VERSION the server speaks. Mismatch → the client force-refreshes. */
  protocolVersion: number;
  /**
   * Build fingerprint (net/fingerprint.ts): the client rejects a server whose
   * simulationVersion or constantsHash differ from its own even when the wire
   * layout (protocolVersion) matches — a tuning change to constants.ts leaves the
   * bytes identical but the game is different (§15.2). gitCommit is metadata only
   * and never gates play. Added in protocol v3.
   */
  simulationVersion: number;
  constantsHash: string;
  gitCommit: string;
  /** The PlayerId this client drives (index into the SoA arrays). */
  assignedPlayerId: number;
  /** Team 0 (A) or 1 (B). */
  team: 0 | 1;
  /** 0-based slot within the team. */
  slot: number;
  /** Match "mode" = squad size (§9.2); pitch + rule defaults derive from it. */
  squadSize: SquadSize;
  /** The deterministic match seed (§15.3) — the RNG stream depends on it. */
  seed: number;
  hasGoalkeepers: boolean;
  offsideEnabled: boolean;
  foulsEnabled: boolean;
  /** Wet pitch modifies friction/restitution (§3.2) — affects the sim, so sent. */
  wetPitch: boolean;
  varEnabled: boolean;
  /** Air density (altitude) — affects ball flight (§3.2), so it is sent as f32. */
  airDensity: number;
  /** Seconds per half (§5.5). */
  halfLengthS: number;
  substitutionsPerTeam: number;
  /**
   * §4.8: switching is disabled ONLY for a full-human team ("you are your
   * player", the Pro-Clubs experience). Every mode with AI fill — co-op vs COM,
   * 2v2 vs COM, 11v11 with AI-fill — keeps it on. The client cannot infer this
   * (squadSize does not say how many seats are human), so the server states it.
   */
  switchingEnabled: boolean;
  /**
   * Per active player: the static data snapshots omit. Applied to the client's
   * world before its first predict so prediction runs the server's real stats,
   * not a neutral 50-across placeholder. See the header.
   */
  roster: RosterEntry[];
}

/** One player's static, snapshot-omitted data, carried once in the Welcome. */
export interface RosterEntry {
  playerId: number;
  position: Position;
  role: Role;
  attributes: Attributes;
}

const WELCOME_PROTOCOL_BITS = 16;
const WELCOME_SIM_VERSION_BITS = 16;
const WELCOME_PLAYER_BITS = 8; // 0..21, headroom to 255

/**
 * Length-prefixed utf8 string on the wire: 1 byte count (0..255) + the bytes.
 * Used for the fingerprint's constantsHash (8 hex chars) and gitCommit (a short
 * hash or 'dev'). A count over 255 is clamped — neither field is ever near it.
 */
function writeWireString(w: BitWriter, s: string): void {
  const bytes = utf8Encoder.encode(s);
  const n = bytes.length > 255 ? 255 : bytes.length;
  w.writeBits(n, 8);
  for (let i = 0; i < n; i++) w.writeBits(bytes[i]!, 8);
}
function readWireString(r: BitReader): string {
  const n = r.readBits(8);
  const bytes = new Uint8Array(n);
  for (let i = 0; i < n; i++) bytes[i] = r.readBits(8);
  return utf8Decoder.decode(bytes);
}
const WELCOME_TEAM_BITS = 8;
const WELCOME_SLOT_BITS = 8;
const WELCOME_SQUAD_BITS = 8; // squad size value (1,2,3,4,5,7,11)
const WELCOME_SEED_BITS = 32;
const WELCOME_HALF_LENGTH_BITS = 16; // seconds; 2^16 ≈ 18h, ample
const WELCOME_SUBS_BITS = 8;

// ---------------------------------------------------------------------------
// Roster codec. Order below IS the protocol — append only, never reorder, or a
// client decodes the wrong stat into the wrong field. (§8.1, §8.2)
// ---------------------------------------------------------------------------

const WELCOME_ROSTER_COUNT_BITS = 8; // 0..MAX_PLAYERS (22)
const WELCOME_POSITION_BITS = 4; // 15 positions
const WELCOME_ROLE_BITS = 4; // 10 roles
const WELCOME_ATTRIBUTE_BITS = 8; // each attribute is a 0..100 integer

/** Position enum on the wire: the index IS the code. types.ts is a string union,
 *  not a numeric enum, so this ordered table is where the numbering lives. */
const POSITION_ORDER: readonly Position[] = [
  'GK', 'CB', 'LB', 'RB', 'LWB', 'RWB', 'CDM', 'CM', 'CAM', 'LM', 'RM', 'LW', 'RW', 'CF', 'ST',
];
/** Role enum on the wire. Same reasoning as POSITION_ORDER. */
const ROLE_ORDER: readonly Role[] = [
  'Standard', 'BallPlayingCB', 'InvertedFullback', 'BoxToBox', 'Regista',
  'False9', 'Poacher', 'TargetMan', 'InsideForward', 'SweeperKeeper',
];
/**
 * Attribute field order on the wire — matches the Attributes interface in
 * types.ts exactly (§8.1). 34 fields; each is a 0..100 integer sent as u8.
 * Reordering this silently swaps stats between fields on decode.
 */
const ATTRIBUTE_ORDER: readonly (keyof Attributes)[] = [
  // PHYSICAL
  'pace', 'acceleration', 'stamina', 'strength', 'agility', 'balance', 'jumping',
  // TECHNICAL
  'passing', 'shooting', 'dribbling', 'firstTouch', 'crossing', 'finishing',
  'longShots', 'freeKick', 'penalties', 'heading',
  // MENTAL
  'vision', 'composure', 'positioning', 'reactions', 'aggression', 'workRate',
  // DEFENSIVE
  'tackling', 'marking', 'interceptions', 'standingTackle', 'slidingTackle',
  // GOALKEEPER
  'reflexes', 'handling', 'diving', 'kicking', 'gkPositioning', 'oneOnOne',
];

function positionCode(pos: Position): number {
  const idx = POSITION_ORDER.indexOf(pos);
  return idx < 0 ? 0 : idx; // unknown → GK(0); never happens with a real roster
}
function roleCode(role: Role): number {
  const idx = ROLE_ORDER.indexOf(role);
  return idx < 0 ? 0 : idx; // unknown → Standard(0)
}
/** Clamp+round an attribute into the u8 wire range. Values are already 0..100. */
function attrU8(v: number): number {
  const r = Math.round(v);
  return r < 0 ? 0 : r > 255 ? 255 : r;
}

/** Write the Welcome payload into `w` (no type byte — protocol.ts adds it). */
export function encodeWelcome(w: BitWriter, p: WelcomePayload): void {
  w.writeBits(p.protocolVersion & 0xffff, WELCOME_PROTOCOL_BITS);
  // Build fingerprint (v3): read early so a mismatch is legible before the seat.
  w.writeBits(p.simulationVersion & 0xffff, WELCOME_SIM_VERSION_BITS);
  writeWireString(w, p.constantsHash);
  writeWireString(w, p.gitCommit);
  w.writeBits(p.assignedPlayerId & 0xff, WELCOME_PLAYER_BITS);
  w.writeBits(p.team & 0xff, WELCOME_TEAM_BITS);
  w.writeBits(p.slot & 0xff, WELCOME_SLOT_BITS);
  w.writeBits(p.squadSize & 0xff, WELCOME_SQUAD_BITS);
  w.writeBits(p.seed >>> 0, WELCOME_SEED_BITS);
  w.writeBool(p.hasGoalkeepers);
  w.writeBool(p.offsideEnabled);
  w.writeBool(p.foulsEnabled);
  w.writeBool(p.wetPitch);
  w.writeBool(p.varEnabled);
  w.writeFloat(p.airDensity);
  w.writeBits(p.halfLengthS & 0xffff, WELCOME_HALF_LENGTH_BITS);
  w.writeBits(p.substitutionsPerTeam & 0xff, WELCOME_SUBS_BITS);
  w.writeBool(p.switchingEnabled);

  const roster = p.roster;
  const count = roster.length > MAX_PLAYERS ? MAX_PLAYERS : roster.length;
  w.writeBits(count, WELCOME_ROSTER_COUNT_BITS);
  for (let i = 0; i < count; i++) {
    const e = roster[i]!;
    w.writeBits(e.playerId & 0xff, WELCOME_PLAYER_BITS);
    w.writeBits(positionCode(e.position), WELCOME_POSITION_BITS);
    w.writeBits(roleCode(e.role), WELCOME_ROLE_BITS);
    const a = e.attributes;
    for (let k = 0; k < ATTRIBUTE_ORDER.length; k++) {
      w.writeBits(attrU8(a[ATTRIBUTE_ORDER[k]!]), WELCOME_ATTRIBUTE_BITS);
    }
  }
}

/** Read a Welcome payload from `r`. Check `r.overflow` afterwards. */
export function decodeWelcome(r: BitReader): WelcomePayload {
  const protocolVersion = r.readBits(WELCOME_PROTOCOL_BITS);
  const simulationVersion = r.readBits(WELCOME_SIM_VERSION_BITS);
  const constantsHash = readWireString(r);
  const gitCommit = readWireString(r);
  const assignedPlayerId = r.readBits(WELCOME_PLAYER_BITS);
  const team = r.readBits(WELCOME_TEAM_BITS) as 0 | 1;
  const slot = r.readBits(WELCOME_SLOT_BITS);
  const squadSize = r.readBits(WELCOME_SQUAD_BITS) as SquadSize;
  const seed = r.readBits(WELCOME_SEED_BITS) >>> 0;
  const hasGoalkeepers = r.readBool();
  const offsideEnabled = r.readBool();
  const foulsEnabled = r.readBool();
  const wetPitch = r.readBool();
  const varEnabled = r.readBool();
  const airDensity = r.readFloat();
  const halfLengthS = r.readBits(WELCOME_HALF_LENGTH_BITS);
  const substitutionsPerTeam = r.readBits(WELCOME_SUBS_BITS);
  const switchingEnabled = r.readBool();

  let count = r.readBits(WELCOME_ROSTER_COUNT_BITS);
  if (count > MAX_PLAYERS) count = MAX_PLAYERS; // a corrupt count cannot force a wild loop
  const roster: RosterEntry[] = [];
  for (let i = 0; i < count; i++) {
    const playerId = r.readBits(WELCOME_PLAYER_BITS);
    const position = POSITION_ORDER[r.readBits(WELCOME_POSITION_BITS)] ?? 'CM';
    const role = ROLE_ORDER[r.readBits(WELCOME_ROLE_BITS)] ?? 'Standard';
    const attributes = readAttributes(r);
    roster.push({ playerId, position, role, attributes });
  }

  return {
    protocolVersion,
    simulationVersion,
    constantsHash,
    gitCommit,
    assignedPlayerId,
    team,
    slot,
    squadSize,
    seed,
    hasGoalkeepers,
    offsideEnabled,
    foulsEnabled,
    wetPitch,
    varEnabled,
    airDensity,
    halfLengthS,
    substitutionsPerTeam,
    switchingEnabled,
    roster,
  };
}

/** Read the 34 attribute u8s in wire order into a fresh Attributes object. */
function readAttributes(r: BitReader): Attributes {
  const a = {} as Attributes;
  for (let k = 0; k < ATTRIBUTE_ORDER.length; k++) {
    a[ATTRIBUTE_ORDER[k]!] = r.readBits(WELCOME_ATTRIBUTE_BITS);
  }
  return a;
}

/**
 * The reconciliation-seq a snapshot carries is the client's
 * lastProcessedInputSeq (§2.4). A client that has sent no input yet has none, so
 * this value names "before any of your input" for the Welcome-time full
 * snapshot. Client input seq numbering therefore starts at 1. Kept here (not
 * inlined) so the server and the client agree on it.
 */
export const NO_INPUT_SEQ = 0;

// ---------------------------------------------------------------------------
// Roster <-> world helpers (server builds it, client applies it)
// ---------------------------------------------------------------------------

/**
 * Build the Welcome roster from the server's world: one entry per ACTIVE player,
 * carrying the static data snapshots omit. The server calls this once per join.
 */
export function rosterFromWorld(world: WorldState): RosterEntry[] {
  const out: RosterEntry[] = [];
  const p = world.players;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1) continue;
    out.push({
      playerId: i,
      position: world.positions[i]!,
      role: world.roles[i]!,
      attributes: world.attributes[i]!,
    });
  }
  return out;
}

/**
 * Apply a received roster onto the client's world so its prediction runs the
 * server's real stats (§8.1) and real positions (§4.8 GK exclusion). Call this on
 * the template world BEFORE constructing the prediction system, so the ring
 * worlds — which share attributes/positions/roles by reference (world.ts, D4) —
 * all see the real values from tick zero.
 *
 * Attributes are copied field-by-field into the existing per-seat object rather
 * than replaced, so any reference the world already holds stays valid. Only the
 * static, snapshot-omitted data is written here; active/team and all kinematics
 * come from the full snapshot that follows the Welcome.
 */
export function applyRosterToWorld(world: WorldState, roster: readonly RosterEntry[]): void {
  for (let i = 0; i < roster.length; i++) {
    const e = roster[i]!;
    const id = e.playerId;
    if (id < 0 || id >= MAX_PLAYERS) continue;
    world.positions[id] = e.position;
    world.roles[id] = e.role;
    const dst = world.attributes[id]!;
    for (let k = 0; k < ATTRIBUTE_ORDER.length; k++) {
      const key = ATTRIBUTE_ORDER[k]!;
      dst[key] = e.attributes[key];
    }
  }
}
